import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { CloudUpload, FileText, LogIn, Menu } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { marketingUrl } from '../lib/marketingUrls';
import { DRAWER_WIDTH, PAGE_TITLE_KEYS, getProfileMenuItems } from '../lib/navConfig';
import LanguageSelect from './LanguageSelect';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

type NavbarVariant = 'app' | 'public' | 'focus';

interface NavbarProps {
  variant?: NavbarVariant;
  onDrawerToggle?: () => void;
  drawerOpen?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({
  variant = 'app',
  onDrawerToggle,
  drawerOpen = true,
}) => {
  const [accountOpen, setAccountOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const { t } = useLanguage();
  const isDesktop = useIsDesktop();
  const location = useLocation();
  const navigate = useNavigate();

  const pageTitleKey = PAGE_TITLE_KEYS[location.pathname];
  const showUploadCta = variant === 'app' && isAuthenticated && location.pathname !== '/upload';

  const handleLogout = () => {
    logout();
    setAccountOpen(false);
    navigate('/login');
  };

  const profileMenuItems = getProfileMenuItems('app');

  const accountMenu = isAuthenticated ? (
    <DropdownMenu open={accountOpen} onOpenChange={setAccountOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="rounded-full border-2 border-transparent hover:border-border"
          aria-label={t('profile')}
        >
          <Avatar className="h-9 w-9">
            <AvatarFallback>{user?.name?.[0]?.toUpperCase() || 'U'}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col">
          <span className="font-semibold">
            {user?.name} {user?.surname}
          </span>
          <span className="text-xs font-normal text-muted-foreground">{user?.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {profileMenuItems.map((item, index) => {
          const Icon = item.icon;
          const showSeparatorBefore = item.action === 'logout' && index > 0;

          if (item.type === 'app' && item.path) {
            return (
              <DropdownMenuItem key={item.id} asChild>
                <RouterLink to={item.path} className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  {t(item.labelKey)}
                </RouterLink>
              </DropdownMenuItem>
            );
          }

          if (item.type === 'marketing' && item.marketingPath) {
            return (
              <DropdownMenuItem key={item.id} asChild>
                <a href={marketingUrl(item.marketingPath)} className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  {t(item.labelKey)}
                </a>
              </DropdownMenuItem>
            );
          }

          if (item.action === 'logout') {
            return (
              <React.Fragment key={item.id}>
                {showSeparatorBefore && <DropdownMenuSeparator />}
                <DropdownMenuItem className="cursor-pointer" onClick={handleLogout}>
                  <Icon className="mr-2 h-4 w-4" />
                  {t(item.labelKey)}
                </DropdownMenuItem>
              </React.Fragment>
            );
          }

          return null;
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : (
    <Button asChild size="sm">
      <RouterLink to="/login" className="flex items-center gap-2">
        <LogIn className="h-4 w-4" />
        <span className="text-sm">{t('login')}</span>
      </RouterLink>
    </Button>
  );

  const shellStyle =
    (variant === 'app' || variant === 'focus') && drawerOpen && isDesktop
      ? {
          width: `calc(100% - ${DRAWER_WIDTH}px)`,
          marginLeft: DRAWER_WIDTH,
        }
      : undefined;

  if (variant === 'public') {
    return (
      <header className="fixed top-0 z-40 h-16 w-full border-b bg-background/95 backdrop-blur">
        <div className="container mx-auto flex h-full items-center justify-between gap-4 px-4">
          <RouterLink to="/" className="flex items-center gap-2 text-base font-medium">
            <FileText className="h-5 w-5 text-primary" />
            mySign.az
          </RouterLink>
          <div className="flex items-center gap-2">
            <LanguageSelect triggerClassName="w-[140px]" />
            {accountMenu}
          </div>
        </div>
      </header>
    );
  }

  return (
    <header
      className="fixed top-0 z-40 h-16 w-full border-b bg-background/95 backdrop-blur transition-all"
      style={shellStyle}
    >
      <div className="flex h-full items-center gap-2 px-4 md:gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="shrink-0 md:hidden"
          onClick={onDrawerToggle}
          aria-label={t('menu')}
        >
          <Menu className="h-5 w-5" />
        </Button>

        <div className="min-w-0 flex-1">
          {pageTitleKey && (
            <h1 className="truncate text-base font-semibold">{t(pageTitleKey)}</h1>
          )}
        </div>

        {showUploadCta && (
          <Button asChild size="icon" className="shrink-0 md:hidden" aria-label={t('uploadDocument')}>
            <RouterLink to="/upload">
              <CloudUpload className="h-4 w-4" />
            </RouterLink>
          </Button>
        )}
        {showUploadCta && (
          <Button asChild size="sm" className="hidden shrink-0 md:inline-flex">
            <RouterLink to="/upload" className="flex items-center gap-2">
              <CloudUpload className="h-4 w-4" />
              {t('uploadDocument')}
            </RouterLink>
          </Button>
        )}
      </div>
    </header>
  );
};

export default Navbar;
